/**
 * AppProviders - Composite provider pattern
 *
 * Combines all global context providers into a single component.
 * Reduces nesting in App.jsx and centralizes provider configuration.
 *
 * Provider order matters - providers lower in the tree can access
 * values from providers higher in the tree:
 * 1. AmbientEffectsProvider - No dependencies
 * 2. ToastProvider - No dependencies
 * 3. ConfettiProvider - May use toast
 * 4. LootPopProvider - May use confetti
 * 5. CurrentActionProvider - Reads auth state (must sit INSIDE AuthProvider;
 *    it gates its /player/state fetch on the session so pre-auth 403s never
 *    hit the console on the public login page — M26)
 *
 * Note: CelebrationProvider requires Router context, so it's wrapped
 * separately inside the Router in App.jsx
 */

import { ToastProvider } from '../contexts/ToastContext'
import { ConfettiProvider } from '../contexts/ConfettiContext'
import { AmbientEffectsProvider } from '../contexts/AmbientEffectsContext'
import { LootPopProvider } from '../contexts/LootPopContext'

/**
 * Composes multiple providers into a single wrapper
 * @param {Array} providers - Array of [Provider, props] tuples
 * @param {ReactNode} children - Child components
 */
function composeProviders(providers, children) {
  return providers.reduceRight(
    (acc, [Provider, props = {}]) => <Provider {...props}>{acc}</Provider>,
    children
  )
}

/**
 * Root-level providers (don't require Router context)
 */
export function RootProviders({ children }) {
  const providers = [
    [AmbientEffectsProvider],
    [ToastProvider],
    [ConfettiProvider],
    [LootPopProvider],
  ]

  return composeProviders(providers, children)
}


/**
 * Router-dependent providers (require Router context)
 * Used inside the Router component
 */
export { CelebrationProvider } from '../contexts/CelebrationContext'

export default RootProviders
